/*
 * @Author: Yumeng Xue
 * @Date: 2025-11-28
 * @Description: 
 * 局部线条向量化器 (Local Vectorization)
 * 
 * 用于提取用户选中区域内线条的局部形状特征向量。
 * 支持两种模式：
 * - 1D 时间序列：提取 Y 值序列
 * - 2D 轨迹：提取 (x, y) 坐标序列
 * 
 * 主要应用场景：
 * - 局部线条聚类
 * - 形状相似性分析
 * - 异常检测
 * - 降维可视化（如 PCA, t-SNE）
 * 
 * @FilePath: /topo-mess/src/core/local-vectorization.ts
 */

import { LineData } from "./binning";

// 定义本地的模式类型，不再依赖 TdaScanner
export type LocalVectorizationMode = "1D_TimeSeries" | "2D_Trajectory";

/**
 * 锚点信息：记录每条线在选中区域的参考位置
 */
export interface AnchorInfo {
    lineId: number;      // 线条 ID
    index: number;       // 锚点在线条轨迹中的索引位置
    t?: number;          // (可选) 1D 模式下的时间值
    s?: number;          // (可选) 2D 模式下的弧长值
}

/**
 * 特征向量结果
 */
export interface FeatureVectorResult {
    vectors: number[][];     // 特征向量矩阵 [N × K] 或 [N × 2K]
    lineIds: number[];       // 对应的线条 ID
    metadata: {
        mode: LocalVectorizationMode;
        windowSize: number;
        sampleCount: number;
        vectorDim: number;   // 每个向量的维度
        normalized: boolean;
    };
}

/**
 * 局部向量化器类
 */
export class LocalVectorization {

    /**
     * 提取选中线条的局部形状特征向量
     * 
     * @param lines - 所有线条数据
     * @param selectedIds - 用户选中的线条 ID 列表
     * @param anchors - 每条线的锚点信息（Map: lineId → AnchorInfo）
     * @param w - 窗口半径（前后瞻距离）
     * @param sampleCount - 采样点数量
     * @param mode - 模式（1D_TimeSeries 或 2D_Trajectory）
     * @param normalize - 是否进行 Z-Score 归一化（默认 true）
     * @returns FeatureVectorResult - 包含特征向量矩阵和元数据
     */
    public static getFeatureVectors(
        lines: LineData[],
        selectedIds: number[],
        anchors: Map<number, AnchorInfo>,
        w: number,
        sampleCount: number,
        mode: LocalVectorizationMode,
        normalize: boolean = true
    ): FeatureVectorResult {

        const vectors: number[][] = [];
        const validLineIds: number[] = [];

        // ======================================================================
        // 遍历每条选中的线条，提取其局部特征向量
        // ======================================================================
        for (const lineId of selectedIds) {
            const line = lines[lineId];
            const anchor = anchors.get(lineId);

            // 验证数据完整性
            if (!line || !anchor || !line.xValues || line.xValues.length < 2) {
                console.warn(`Skipping line ${lineId}: invalid data or anchor`);
                continue;
            }

            // 根据模式提取特征向量
            let featureVector: number[] | null = null;

            if (mode === "1D_TimeSeries") {
                featureVector = this.extract1DFeatures(line, anchor, w, sampleCount);
            } else if (mode === "2D_Trajectory") {
                featureVector = this.extract2DFeatures(line, anchor, w, sampleCount);
            }

            // 如果提取成功，添加到结果中
            if (featureVector && featureVector.length > 0) {
                vectors.push(featureVector);
                validLineIds.push(lineId);
            }
        }

        // ======================================================================
        // 归一化处理（可选）
        // ======================================================================
        let finalVectors = vectors;
        if (normalize && vectors.length > 0) {
            finalVectors = this.normalizeVectors(vectors);
        }

        // ======================================================================
        // 返回结果
        // ======================================================================
        const vectorDim = finalVectors.length > 0 ? finalVectors[0].length : 0;

        return {
            vectors: finalVectors,
            lineIds: validLineIds,
            metadata: {
                mode,
                windowSize: w,
                sampleCount,
                vectorDim,
                normalized: normalize
            }
        };
    }

    /**
     * 提取 1D 时间序列模式的特征向量
     * 
     * 策略：
     * 1. 在锚点的时间 t_center 周围，提取 [t_center - w, t_center + w] 范围
     * 2. 在此范围内均匀采样 sampleCount 个时间点
     * 3. 对每个时间点，使用线性插值获取对应的 Y 值
     * 
     * @param line - 线条数据
     * @param anchor - 锚点信息
     * @param w - 窗口半径
     * @param sampleCount - 采样点数
     * @returns 特征向量 [sampleCount × 1]
     */
    private static extract1DFeatures(
        line: LineData,
        anchor: AnchorInfo,
        w: number,
        sampleCount: number
    ): number[] | null {

        const { index } = anchor;

        // ----------------------------------------------------------------------
        // 1. 确定时间轴范围
        // ----------------------------------------------------------------------
        // 如果有显式时间戳，使用时间戳；否则使用 X 值作为时间轴
        const timeAxis = line.times || line.xValues;

        if (index < 0 || index >= timeAxis.length) {
            return null;
        }

        const t_center = timeAxis[index];
        const t_start = t_center - w;
        const t_end = t_center + w;

        // ----------------------------------------------------------------------
        // 2. 在窗口内均匀采样
        // ----------------------------------------------------------------------
        const featureVector: number[] = [];
        const step = (2 * w) / (sampleCount - 1);

        for (let i = 0; i < sampleCount; i++) {
            const t = t_start + i * step;

            // 使用线性插值获取 Y 值
            const y = this.interpolateY(timeAxis, line.yValues, t);

            if (y !== null) {
                featureVector.push(y);
            } else {
                // 如果超出范围，使用最近的边界值
                if (t < timeAxis[0]) {
                    featureVector.push(line.yValues[0]);
                } else {
                    featureVector.push(line.yValues[line.yValues.length - 1]);
                }
            }
        }

        return featureVector;
    }

    /**
     * 提取 2D 轨迹模式的特征向量
     * 
     * 策略：
     * 1. 计算线条的累积弧长（arc length）
     * 2. 在锚点的弧长 s_center 周围，提取 [s_center - w, s_center + w] 范围
     * 3. 在此范围内均匀采样 sampleCount 个弧长点
     * 4. 对每个弧长点，使用线性插值获取对应的 (x, y) 坐标
     * 5. 将坐标展平为向量 [x1, y1, x2, y2, ..., xK, yK]
     * 
     * @param line - 线条数据
     * @param anchor - 锚点信息
     * @param w - 窗口半径
     * @param sampleCount - 采样点数
     * @returns 特征向量 [sampleCount × 2]（展平）
     */
    private static extract2DFeatures(
        line: LineData,
        anchor: AnchorInfo,
        w: number,
        sampleCount: number
    ): number[] | null {

        const { index } = anchor;

        if (index < 0 || index >= line.xValues.length) {
            return null;
        }

        // ----------------------------------------------------------------------
        // 1. 计算累积弧长
        // ----------------------------------------------------------------------
        const arcLengths = this.computeArcLengths(line.xValues, line.yValues);
        const s_center = arcLengths[index];
        const s_start = s_center - w;
        const s_end = s_center + w;

        // ----------------------------------------------------------------------
        // 2. 在弧长范围内均匀采样
        // ----------------------------------------------------------------------
        const featureVector: number[] = [];
        const step = (2 * w) / (sampleCount - 1);

        for (let i = 0; i < sampleCount; i++) {
            const s = s_start + i * step;

            // 使用线性插值获取 (x, y) 坐标
            const point = this.interpolatePoint(arcLengths, line.xValues, line.yValues, s);

            if (point) {
                featureVector.push(point.x);
                featureVector.push(point.y);
            } else {
                // 如果超出范围，使用最近的边界点
                if (s < arcLengths[0]) {
                    featureVector.push(line.xValues[0]);
                    featureVector.push(line.yValues[0]);
                } else {
                    const lastIdx = line.xValues.length - 1;
                    featureVector.push(line.xValues[lastIdx]);
                    featureVector.push(line.yValues[lastIdx]);
                }
            }
        }

        return featureVector;
    }

    /**
     * 计算线条的累积弧长
     * 
     * 弧长 s[i] = Σ(从 0 到 i-1) √((x[j+1] - x[j])² + (y[j+1] - y[j])²)
     * 
     * @param xValues - X 坐标数组
     * @param yValues - Y 坐标数组
     * @returns 累积弧长数组
     */
    private static computeArcLengths(xValues: number[], yValues: number[]): number[] {
        const arcLengths: number[] = [0]; // 起点弧长为 0
        let cumulativeLength = 0;

        for (let i = 1; i < xValues.length; i++) {
            const dx = xValues[i] - xValues[i - 1];
            const dy = yValues[i] - yValues[i - 1];
            const segmentLength = Math.sqrt(dx * dx + dy * dy);

            cumulativeLength += segmentLength;
            arcLengths.push(cumulativeLength);
        }

        return arcLengths;
    }

    /**
     * 线性插值获取指定时间/X值对应的 Y 值
     * 
     * 使用二分查找定位插值区间，然后进行线性插值
     * 
     * @param xAxis - X 轴数组（时间或 X 坐标）
     * @param yValues - Y 值数组
     * @param targetX - 目标 X 值
     * @returns 插值得到的 Y 值，如果超出范围则返回 null
     */
    private static interpolateY(
        xAxis: number[],
        yValues: number[],
        targetX: number
    ): number | null {

        // 边界检查
        if (targetX < xAxis[0] || targetX > xAxis[xAxis.length - 1]) {
            return null;
        }

        // 二分查找定位插值区间
        let left = 0;
        let right = xAxis.length - 1;

        while (left < right - 1) {
            const mid = Math.floor((left + right) / 2);
            if (xAxis[mid] <= targetX) {
                left = mid;
            } else {
                right = mid;
            }
        }

        // 线性插值
        const x0 = xAxis[left];
        const x1 = xAxis[right];
        const y0 = yValues[left];
        const y1 = yValues[right];

        if (x1 === x0) {
            return y0; // 避免除以零
        }

        const t = (targetX - x0) / (x1 - x0);
        return y0 + t * (y1 - y0);
    }

    /**
     * 根据弧长插值获取 (x, y) 坐标
     * 
     * @param arcLengths - 累积弧长数组
     * @param xValues - X 坐标数组
     * @param yValues - Y 坐标数组
     * @param targetS - 目标弧长
     * @returns 插值得到的坐标点，如果超出范围则返回 null
     */
    private static interpolatePoint(
        arcLengths: number[],
        xValues: number[],
        yValues: number[],
        targetS: number
    ): { x: number; y: number } | null {

        // 边界检查
        if (targetS < arcLengths[0] || targetS > arcLengths[arcLengths.length - 1]) {
            return null;
        }

        // 二分查找定位插值区间
        let left = 0;
        let right = arcLengths.length - 1;

        while (left < right - 1) {
            const mid = Math.floor((left + right) / 2);
            if (arcLengths[mid] <= targetS) {
                left = mid;
            } else {
                right = mid;
            }
        }

        // 线性插值
        const s0 = arcLengths[left];
        const s1 = arcLengths[right];

        if (s1 === s0) {
            return { x: xValues[left], y: yValues[left] }; // 避免除以零
        }

        const t = (targetS - s0) / (s1 - s0);
        const x = xValues[left] + t * (xValues[right] - xValues[left]);
        const y = yValues[left] + t * (yValues[right] - yValues[left]);

        return { x, y };
    }

    /**
     * Z-Score 归一化（标准化）
     * 
     * 对每个维度独立进行归一化：
     * normalized[i][j] = (vectors[i][j] - mean[j]) / std[j]
     * 
     * 优势：
     * - 消除量纲影响，使得不同维度可比
     * - 突出"形状"特征而非"绝对位置"
     * - 适合后续的距离计算和聚类
     * 
     * @param vectors - 原始特征向量矩阵 [N × K]
     * @returns 归一化后的特征向量矩阵 [N × K]
     */
    private static normalizeVectors(vectors: number[][]): number[][] {
        if (vectors.length === 0 || vectors[0].length === 0) {
            return vectors;
        }

        const numVectors = vectors.length;
        const vectorDim = vectors[0].length;

        // ----------------------------------------------------------------------
        // 1. 计算每个维度的均值和标准差
        // ----------------------------------------------------------------------
        const means: number[] = new Array(vectorDim).fill(0);
        const stds: number[] = new Array(vectorDim).fill(0);

        // 计算均值
        for (let j = 0; j < vectorDim; j++) {
            let sum = 0;
            for (let i = 0; i < numVectors; i++) {
                sum += vectors[i][j];
            }
            means[j] = sum / numVectors;
        }

        // 计算标准差
        for (let j = 0; j < vectorDim; j++) {
            let sumSquaredDiff = 0;
            for (let i = 0; i < numVectors; i++) {
                const diff = vectors[i][j] - means[j];
                sumSquaredDiff += diff * diff;
            }
            stds[j] = Math.sqrt(sumSquaredDiff / numVectors);
        }

        // ----------------------------------------------------------------------
        // 2. 归一化
        // ----------------------------------------------------------------------
        const normalizedVectors: number[][] = [];

        for (let i = 0; i < numVectors; i++) {
            const normalizedVector: number[] = [];
            for (let j = 0; j < vectorDim; j++) {
                // 如果标准差为 0（该维度所有值相同），则归一化为 0
                if (stds[j] === 0) {
                    normalizedVector.push(0);
                } else {
                    const normalized = (vectors[i][j] - means[j]) / stds[j];
                    normalizedVector.push(normalized);
                }
            }
            normalizedVectors.push(normalizedVector);
        }

        return normalizedVectors;
    }

    /**
     * 计算两个向量之间的欧氏距离
     * 
     * @param v1 - 向量 1
     * @param v2 - 向量 2
     * @returns 欧氏距离
     */
    public static euclideanDistance(v1: number[], v2: number[]): number {
        if (v1.length !== v2.length) {
            throw new Error("Vector dimensions must match");
        }

        let sumSquaredDiff = 0;
        for (let i = 0; i < v1.length; i++) {
            const diff = v1[i] - v2[i];
            sumSquaredDiff += diff * diff;
        }

        return Math.sqrt(sumSquaredDiff);
    }

    /**
     * 计算两个向量之间的余弦相似度
     * 
     * @param v1 - 向量 1
     * @param v2 - 向量 2
     * @returns 余弦相似度 [-1, 1]
     */
    public static cosineSimilarity(v1: number[], v2: number[]): number {
        if (v1.length !== v2.length) {
            throw new Error("Vector dimensions must match");
        }

        let dotProduct = 0;
        let norm1 = 0;
        let norm2 = 0;

        for (let i = 0; i < v1.length; i++) {
            dotProduct += v1[i] * v2[i];
            norm1 += v1[i] * v1[i];
            norm2 += v2[i] * v2[i];
        }

        const denominator = Math.sqrt(norm1) * Math.sqrt(norm2);
        return denominator === 0 ? 0 : dotProduct / denominator;
    }
}
